<div class="min-h-screen flex flex-col font-sans bg-cloud-mist">

  <main class="flex-1">
    <div class="tfr-page-shell py-8 sm:py-12">

      <!-- Header + search bar -->
      <div class="mb-6">
        <h1 class="text-[26px] sm:text-[30px] font-bold leading-[1.2] text-midnight-indigo mb-1.5">
          Espacios disponibles en toda Espa&ntilde;a
        </h1>
        <p class="text-[14px] leading-[1.6] text-slate-blue mb-5">
          Selecciona un espacio para ver disponibilidad y reservar.
        </p>

        <!-- Search + filter toggle row -->
        <div class="flex items-center gap-3">
          <!-- Search input -->
          <div class="relative flex-1 max-w-lg">
            <svg class="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-steel-gray"
                 xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                 stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round"
                    d="M21 21l-4.35-4.35M17 11A6 6 0 105 11a6 6 0 0012 0z"/>
            </svg>
            <input type="search"
                   [ngModel]="searchQuery()"
                   (ngModelChange)="searchQuery.set($event)"
                   placeholder="Buscar por zona, nombre o actividad..."
                   class="w-full pl-10 pr-4 py-2.5 rounded-xl border border-pale-gray bg-white
                          text-[14px] text-midnight-indigo placeholder:text-steel-gray
                          focus:outline-none focus:ring-2 focus:ring-action-blue/30
                          focus:border-action-blue/50 transition-colors shadow-tfr-sm" />
          </div>

          <!-- Filters toggle button -->
          <button type="button" (click)="toggleFilters()"
            class="relative flex items-center gap-2 h-10 px-4 rounded-xl border transition-colors
                   focus:outline-none text-[13px] font-semibold"
            [class.border-midnight-indigo]="activeFilterCount() > 0"
            [class.bg-midnight-indigo]="activeFilterCount() > 0"
            [class.text-white]="activeFilterCount() > 0"
            [class.border-pale-gray]="activeFilterCount() === 0"
            [class.bg-white]="activeFilterCount() === 0"
            [class.text-slate-blue]="activeFilterCount() === 0">
            <lucide-icon name="sliders-horizontal" [size]="15" />
            <span class="hidden sm:inline">Filtros</span>
            @if (activeFilterCount() > 0) {
              <span class="h-4 w-4 rounded-full bg-white/25 text-[11px] font-bold flex items-center justify-center">
                {{ activeFilterCount() }}
              </span>
            }
          </button>
        </div>

        <!-- Expandable filter panel -->
        @if (filtersVisible()) {
          <div class="mt-3 rounded-2xl border border-pale-gray bg-white p-4 shadow-tfr-sm">
            <div class="flex flex-col sm:flex-row gap-4">

              <!-- Category chips -->
              <div class="flex-1">
                <p class="text-[10px] font-semibold uppercase tracking-wider text-steel-gray mb-2">
                  Tipo de espacio
                </p>
                <div class="flex flex-wrap gap-2">
                  <button type="button" (click)="filterCategory.set('')"
                    class="h-8 px-3 rounded-lg border text-[12px] font-medium transition-colors focus:outline-none"
                    [class.bg-midnight-indigo]="filterCategory() === ''"
                    [class.border-midnight-indigo]="filterCategory() === ''"
                    [class.text-white]="filterCategory() === ''"
                    [class.border-pale-gray]="filterCategory() !== ''"
                    [class.text-slate-blue]="filterCategory() !== ''">
                    Todos
                  </button>
                  @for (cat of SPACE_CATEGORIES; track cat) {
                    <button type="button" (click)="filterCategory.set(cat)"
                      class="h-8 px-3 rounded-lg border text-[12px] font-medium transition-colors focus:outline-none"
                      [class.bg-midnight-indigo]="filterCategory() === cat"
                      [class.border-midnight-indigo]="filterCategory() === cat"
                      [class.text-white]="filterCategory() === cat"
                      [class.border-pale-gray]="filterCategory() !== cat"
                      [class.text-slate-blue]="filterCategory() !== cat">
                      {{ SPACE_CATEGORY_LABELS[cat] }}
                    </button>
                  }
                </div>
              </div>

              <!-- Province + capacity -->
              <div class="flex flex-col sm:flex-row gap-3 shrink-0">
                <!-- Province -->
                @if (availableProvinces().length > 0) {
                  <div>
                    <p class="text-[10px] font-semibold uppercase tracking-wider text-steel-gray mb-2">
                      Provincia
                    </p>
                    <div class="relative">
                      <select [ngModel]="filterProvince()" (ngModelChange)="filterProvince.set($event)"
                        class="h-9 pl-3 pr-8 rounded-lg border border-pale-gray bg-cloud-mist
                               text-[13px] text-midnight-indigo appearance-none cursor-pointer
                               focus:outline-none focus:ring-2 focus:ring-action-blue/20">
                        <option value="">Todas</option>
                        @for (prov of availableProvinces(); track prov) {
                          <option [value]="prov">{{ provinceLabel(prov) }}</option>
                        }
                      </select>
                      <span class="pointer-events-none absolute inset-y-0 right-2 flex items-center text-steel-gray">
                        <lucide-icon name="chevron-down" [size]="12" />
                      </span>
                    </div>
                  </div>
                }

                <!-- Min capacity -->
                <div>
                  <p class="text-[10px] font-semibold uppercase tracking-wider text-steel-gray mb-2">
                    Aforo mínimo
                  </p>
                  <div class="relative">
                    <select
                      [ngModel]="filterMinCapacity()"
                      (ngModelChange)="filterMinCapacity.set($event ? +$event : null)"
                      class="h-9 pl-3 pr-8 rounded-lg border border-pale-gray bg-cloud-mist
                             text-[13px] text-midnight-indigo appearance-none cursor-pointer
                             focus:outline-none focus:ring-2 focus:ring-action-blue/20">
                      <option [ngValue]="null">Sin límite</option>
                      <option [ngValue]="10">10+ personas</option>
                      <option [ngValue]="20">20+ personas</option>
                      <option [ngValue]="30">30+ personas</option>
                      <option [ngValue]="50">50+ personas</option>
                      <option [ngValue]="100">100+ personas</option>
                    </select>
                    <span class="pointer-events-none absolute inset-y-0 right-2 flex items-center text-steel-gray">
                      <lucide-icon name="chevron-down" [size]="12" />
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Clear filters -->
            @if (activeFilterCount() > 0) {
              <div class="mt-3 pt-3 border-t border-pale-gray flex justify-end">
                <button type="button" (click)="clearFilters()"
                  class="text-[12px] font-medium text-steel-gray hover:text-midnight-indigo transition-colors
                         inline-flex items-center gap-1.5">
                  <lucide-icon name="x" [size]="13" />
                  Limpiar filtros
                </button>
              </div>
            }
          </div>
        }

        <!-- Active filters summary chips -->
        @if (activeFilterCount() > 0 && !filtersVisible()) {
          <div class="mt-2.5 flex flex-wrap gap-2">
            @if (filterCategory()) {
              <span class="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full bg-midnight-indigo/10 text-midnight-indigo text-[12px] font-medium">
                {{ categoryLabel(filterCategory()) }}
                <button type="button" (click)="filterCategory.set('')" class="text-midnight-indigo/50 hover:text-midnight-indigo">
                  <lucide-icon name="x" [size]="10" />
                </button>
              </span>
            }
            @if (filterProvince()) {
              <span class="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full bg-midnight-indigo/10 text-midnight-indigo text-[12px] font-medium">
                {{ provinceLabel(filterProvince()) }}
                <button type="button" (click)="filterProvince.set('')" class="text-midnight-indigo/50 hover:text-midnight-indigo">
                  <lucide-icon name="x" [size]="10" />
                </button>
              </span>
            }
            @if (filterMinCapacity()) {
              <span class="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full bg-midnight-indigo/10 text-midnight-indigo text-[12px] font-medium">
                {{ filterMinCapacity() }}+ personas
                <button type="button" (click)="filterMinCapacity.set(null)" class="text-midnight-indigo/50 hover:text-midnight-indigo">
                  <lucide-icon name="x" [size]="10" />
                </button>
              </span>
            }
          </div>
        }
      </div>

      <!-- Loading skeleton -->
      @if (loading()) {
        <div class="flex justify-center items-center py-20">
          <svg class="animate-spin h-8 w-8 text-action-blue" xmlns="http://www.w3.org/2000/svg"
            fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
          </svg>
        </div>
      }

      <!-- Empty state: no spaces at all -->
      @if (!loading() && allSpaces().length === 0) {
        <div class="flex flex-col items-center justify-center py-24 text-center">
          <div class="h-16 w-16 rounded-2xl bg-pale-gray flex items-center justify-center mb-6">
            <svg class="h-8 w-8 text-steel-gray" xmlns="http://www.w3.org/2000/svg" fill="none"
              viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
              <path stroke-linecap="round" stroke-linejoin="round"
              d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0
                 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/>
              </svg>
            </div>
            <p class="text-[15px] font-semibold text-midnight-indigo mb-1">Aún no hay espacios publicados</p>
            <p class="text-[13px] text-slate-blue">Vuelve pronto, estamos preparando el catálogo.</p>
          </div>
        }

      <!-- Empty state: no match after filter/search -->
      @if (!loading() && filteredSpaces().length === 0 && allSpaces().length > 0) {
        <div class="flex flex-col items-center justify-center py-20 text-center">
          <div class="h-14 w-14 rounded-2xl bg-pale-gray flex items-center justify-center mb-4">
            <svg class="h-7 w-7 text-steel-gray" xmlns="http://www.w3.org/2000/svg" fill="none"
                 viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
              <path stroke-linecap="round" stroke-linejoin="round"
                    d="M21 21l-4.35-4.35M17 11A6 6 0 105 11a6 6 0 0012 0z"/>
            </svg>
          </div>
          <p class="text-[15px] font-semibold text-midnight-indigo mb-1">Sin resultados</p>
          <p class="text-[13px] text-slate-blue">Prueba con otra zona o actividad.</p>
          @if (activeFilterCount() > 0) {
            <button type="button" (click)="clearFilters()"
              class="mt-4 text-[13px] font-medium text-action-blue hover:underline">
              Limpiar filtros activos
            </button>
          }
        </div>
      }

      <!-- Results count + cards grid -->
      @if (!loading() && filteredSpaces().length > 0) {
        <div class="mb-4 flex items-center justify-between">
          <p class="text-[12px] text-steel-gray">
            {{ filteredSpaces().length }}
            {{ filteredSpaces().length === 1 ? 'espacio encontrado' : 'espacios encontrados' }}
          </p>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          @for (space of filteredSpaces(); track space.id) {
            <app-space-card [space]="space"></app-space-card>
          }
        </div>
      }

    </div>
  </main>

  @if (showFooter()) {
    <app-site-footer />
  }

</div>
